import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useTeacherPandadocDocsQuery,
  TeacherPandadocDocsQuery,
  TeacherPandadocDocsQueryVariables,
} from 'core/graphql';

import { isPermissionDeniedError } from './usePandaDocAnalytics';

type TeacherPandaDocEdge = NonNullable<
  NonNullable<TeacherPandadocDocsQuery['pandadocDocuments']>['edges'][number]
>;
export type TeacherPandaDocRow = NonNullable<TeacherPandaDocEdge['node']>;

export type TeacherPandaDocFilters = Omit<
  TeacherPandadocDocsQueryVariables,
  'first' | 'after'
>;

interface ITeacherPandaDocumentsHook {
  data: TeacherPandaDocRow[];
  totalCount: number;
  loading: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  hasNextPage: boolean;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/**
 * Teacher analog of `usePandaDocDocuments` (spec 0007): relay-cursor paginated
 * list of PandaDoc documents scoped to a teacher, feeding the Договор tab.
 */
export const useTeacherPandaDocuments = (
  filters: TeacherPandaDocFilters = {},
  pageSize = 50,
): ITeacherPandaDocumentsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
  } = useTeacherPandadocDocsQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.pandadocDocuments;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        ...filters,
        first: pageSize,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.pandadocDocuments) {
          return prev;
        }

        return {
          pandadocDocuments: {
            ...fetchMoreResult.pandadocDocuments,
            edges: [
              ...(prev.pandadocDocuments?.edges ?? []),
              ...fetchMoreResult.pandadocDocuments.edges,
            ],
          },
        };
      },
    });
  }, [connection, fetchMore, filters, pageSize]);

  return {
    data: edges
      .map((edge) => edge?.node)
      .filter((node): node is TeacherPandaDocRow => Boolean(node)),
    totalCount: connection?.totalCount ?? 0,
    loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
